import ExcelJS, { CellValue } from "exceljs";
import { saveAs } from "file-saver";
import { isEmpty } from "lodash";
import { useCallback, useRef } from "react";
import { CreateNotification } from "../../../notifier";
import { TemplatesValue } from "../excel-template";
import useExcelStyle from "./useExcelStyle";

type UseExcelTemplateConfig = {
  defaultCharacterLength?: number;
};

const FILE_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const OPTIONS_SHEET = "Sheet2";

export const convertIndexToAlphaBet = (index: number) => (index + 10).toString(36).toUpperCase();

export const useExcelTemplate = (config?: UseExcelTemplateConfig) => {
  const configRef = useRef(config);
  const styles = useExcelStyle();

  const exportFile = useCallback(
    async <T>(params: TemplatesValue<T>) => {
      try {
        const { sheet, structures, fileName, fields } = params;

        /**
         * Create template data
         */
        const templateData = [
          fields.reduce<T>((r, f, i) => {
            r[f] = structures[i].value;
            return r;
          }, {} as T),
        ];

        /**
         * Create Workbook, Worksheet (Sheet1)
         */
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet(sheet);

        /**
         * Add Headers
         */
        const columns: Partial<ExcelJS.Column>[] = [];
        const defaultLength = configRef.current?.defaultCharacterLength ?? 8;

        structures.forEach((s, i) => {
          const { style, header, itemSize } = s;

          const width = templateData.reduce((t, r) => {
            const size = (typeof itemSize === "function" ? itemSize?.(r) : itemSize) ?? 0;
            return Math.max(t, size);
          }, defaultLength);

          const key = fields[i] as string;

          columns.push({ header, key, width, style });
        });

        /**
         * Add Template Data
         */
        if (!isEmpty(columns)) {
          ws.columns = columns;

          templateData.forEach((d) => {
            ws.addRow(d);
          });

          /**
           * Custom styles
           */
          styles(ws, structures);

          /**
           * Data Validation Options
           */
          const dvOps: { options: string[]; cell: string; header: string }[] = [];

          structures.forEach((s, i) => {
            const { options, header } = s;

            if (options) {
              const alphabet = convertIndexToAlphaBet(i); // A, B, C, D, E

              dvOps.push({
                /** For Sheet1 */
                cell: `${alphabet}2`,

                /** For Sheet2 */
                options,
                header,
              });
            }
          });

          /**
           * Sheet2 for data validation
           */
          if (!isEmpty(dvOps)) {
            const wsOpts = wb.addWorksheet(OPTIONS_SHEET);

            dvOps.forEach(({ cell, header, options }, colIndex) => {
              /**
               * Return likes: A, B, C, D, E
               */
              const alphabet = convertIndexToAlphaBet(colIndex);

              /**
               * Set Column Header
               */
              wsOpts.getCell(`${alphabet}1`).value = header;

              /**
               * Create data validation options (Sheet2)
               */
              options.forEach((op, rowIndex) => {
                wsOpts.getCell(`${alphabet}${rowIndex + 2}`).value = op;
              });

              /**
               * add data validation in Sheet1
               */
              ws.getCell(cell).dataValidation = {
                type: "list",
                formulae: [`${OPTIONS_SHEET}!$${alphabet}$2:$${alphabet}$${options.length + 1}`], // cell options will be start from A2, B2, C2,...
              };
            });
          }
        }

        /**
         * Export file
         */
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: FILE_TYPE });
        saveAs(blob, fileName + ".xlsx");
      } catch (error) {
        console.error("🚀 ~ file: useExcelTemplate.ts:137 ~ exportFile ~ error:", error);
      }
    },
    [styles],
  );

  const importFile = useCallback(async <T>(params: { buffer: ExcelJS.Buffer; template: TemplatesValue<T> }) => {
    try {
      const { buffer, template } = params;

      if (!buffer) {
        return;
      }

      /**
       * Load file
       */
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      const ws = wb.getWorksheet(1);

      const data: T[] = [];
      const { fields, structures } = template;

      ws?.eachRow((row, rowNumber) => {
        /**
         * Continue Header
         */
        if (rowNumber === 1) {
          return;
        }

        const obj: T = {} as T;

        row.eachCell((cell, colNumber) => {
          const index = colNumber - 1; // start from 1
          const fieldName = fields[index];
          const { validate } = structures[index];

          (obj[fieldName] as CellValue) = validate(cell.value);
        });

        data.push(obj);
      });

      return data;
    } catch (error) {
      console.error(error);
      CreateNotification({
        title: "Thông báo",
        body: "Đã có lỗi xảy ra!",
        icon: "/icons/warning.png",
      });
    }
  }, []);

  return { exportFile, importFile };
};
